import type { EntryKind, EntrySource } from '@taler/api-client'
import { MONEY_AMOUNT_PATTERN, parseLocalDate } from '@taler/domain'

// EntryResponse.kind/source are optional on the wire, so the generated EntryKind/EntrySource
// aliases carry `| undefined` too; excluded here since a filter value is never "unset".
export type DefiniteEntryKind = Exclude<EntryKind, undefined>
export type DefiniteEntrySource = Exclude<EntrySource, undefined>

export const ENTRY_KINDS: readonly DefiniteEntryKind[] = ['INCOME', 'EXPENSE', 'TRANSFER']
export const ENTRY_SOURCES: readonly DefiniteEntrySource[] = [
  'MANUAL',
  'IMPORT',
  'RECEIPT',
  'CHECKUP',
  'SYSTEM',
  'SCHEDULED',
  'TRANSFER',
  'ACCOUNT_CLOSE',
  'LOAN',
  'FX_REVALUATION',
  'CROSS_LEDGER_FORGIVE',
  'CONTACT_FORGIVE',
]

export function firstValue(raw: unknown): string | undefined {
  const value = Array.isArray(raw) ? raw[0] : raw
  return typeof value === 'string' && value !== '' ? value : undefined
}

export function parseDateParam(raw: unknown): string | undefined {
  const candidate = firstValue(raw)
  if (candidate === undefined) return undefined
  try {
    parseLocalDate(candidate)
    return candidate
  } catch {
    return undefined
  }
}

export function parseKindParam(raw: unknown): DefiniteEntryKind | undefined {
  const candidate = firstValue(raw)
  return candidate !== undefined && (ENTRY_KINDS as readonly string[]).includes(candidate)
    ? (candidate as DefiniteEntryKind)
    : undefined
}

export function parseSourceParam(raw: unknown): DefiniteEntrySource | undefined {
  const candidate = firstValue(raw)
  return candidate !== undefined && (ENTRY_SOURCES as readonly string[]).includes(candidate)
    ? (candidate as DefiniteEntrySource)
    : undefined
}

export function parseAmountParam(raw: unknown): string | undefined {
  const candidate = firstValue(raw)
  return candidate !== undefined && MONEY_AMOUNT_PATTERN.test(candidate) ? candidate : undefined
}

export function parseTextParam(raw: unknown): string | undefined {
  return firstValue(raw)
}

export function parseIdParam(raw: unknown): string | undefined {
  return firstValue(raw)
}

export function parseTagsParam(raw: unknown): string[] | undefined {
  const values = (Array.isArray(raw) ? raw : [raw]).filter(
    (value): value is string => typeof value === 'string' && value !== '',
  )
  return values.length > 0 ? values : undefined
}
